import type { Translate } from "./WorksScoreScreen";

/**
 * The reader-facing wording of the eight risk-of-bias appraisal grids: one
 * entry per domain and per signalling question of
 * `apps/api/src/domain/risk-of-bias/instruments.ts`.
 *
 * The api serves each item's registry English on the wire, next to the stable
 * id it was stored under. That English is MODEL-facing text: it builds the Zod
 * schema the appraisal model answers, so it has to stay the instrument's own
 * validated wording, in one language, forever. The reader needs the same item
 * in the language the rest of the sheet is in, which is a different job, and it
 * is this table.
 *
 * It lives in the webapp, and not in the api, because the card is rendered
 * client-side as one piece and then pinned to one language. That language is
 * the WORK's (`ReadingLanguage`, SIGN-OFF: Menie), so which app the reader is
 * in does not enter into it. The pinning has to happen here whatever this table
 * does: the judgment word, each answer chip, the design label and the headings
 * are normalized enums on the wire by design, so the api cannot state them, and
 * a card whose questions came from the server and whose verdict came from
 * i18next would be two languages again. Once the client pins the language, this
 * table is simply more of what its neighbours already are.
 *
 * Every value calls the translator on a bare string literal, and the parameter
 * is named `t`, because i18next-scanner extracts keys only from call sites
 * spelled exactly that way and prunes every key it cannot see
 * (`removeUnusedKeys`). The scanner reads comments as well as code, which is
 * why this paragraph describes the call shape instead of writing it.
 *
 * Keyed by id rather than by text so a stored verdict keeps its wording when
 * the registry rephrases an item. The key type is `string` because that is what
 * the wire carries: a verdict stored under an older grid can name an item this
 * table has never heard of, and the api projects the same way
 * (`work-evidence-level.ts`). Such an id falls back to the registry English,
 * which is what every reader saw before this table existed.
 *
 * Drift is caught in the api, not here: `instruments.test.ts` asserts that
 * every registry label and question is a key of the webapp's English locale
 * file and is translated in all eight languages. Add a domain to the registry
 * without adding it below and that test fails.
 *
 * Its own file, though `WorksScoreScreen` is its only caller: 187 quoted
 * instrument items are the bulk of the screen's source if inlined, and the
 * module that owns the reader's wording of the eight instruments is the module
 * that mirrors the registry, one entry to one entry.
 *
 * SIGN-OFF: the non-English wording is a Juisci translation, not the
 * instrument authors' own. docs/data-ingestion/risk-of-bias.md carries the
 * review status.
 */
const GRID_TEXT: Record<string, (t: Translate) => string> = {
  // RoB 2 (Cochrane)
  randomization_process: (t) =>
    t("Bias arising from the randomization process"),
  rob2_d1_1: (t) => t("Was the allocation sequence random?"),
  rob2_d1_2: (t) =>
    t(
      "Was the allocation sequence concealed until participants were enrolled and assigned to interventions?"
    ),
  rob2_d1_3: (t) =>
    t(
      "Did baseline differences between intervention groups suggest a problem with the randomization process?"
    ),
  deviations_from_interventions: (t) =>
    t("Bias due to deviations from intended interventions"),
  rob2_d2_1: (t) =>
    t(
      "Were participants aware of their assigned intervention during the trial?"
    ),
  rob2_d2_2: (t) =>
    t(
      "Were carers and people delivering the interventions aware of participants' assigned intervention?"
    ),
  rob2_d2_3: (t) =>
    t(
      "Were there deviations from the intended intervention that arose because of the trial context?"
    ),
  rob2_d2_4: (t) =>
    t("Were these deviations likely to have affected the outcome?"),
  rob2_d2_5: (t) =>
    t(
      "Were these deviations from intended intervention balanced between groups?"
    ),
  rob2_d2_6: (t) =>
    t(
      "Was an appropriate analysis used to estimate the effect of assignment to intervention (e.g. intention-to-treat)?"
    ),
  rob2_d2_7: (t) =>
    t(
      "Was there potential for a substantial impact (on the result) of the failure to analyse participants in the group to which they were randomized?"
    ),
  missing_outcome_data: (t) => t("Bias due to missing outcome data"),
  rob2_d3_1: (t) =>
    t(
      "Were outcome data available for all, or nearly all, randomized participants?"
    ),
  rob2_d3_2: (t) =>
    t(
      "Is there evidence that the result was not biased by missing outcome data?"
    ),
  rob2_d3_3: (t) =>
    t("Could missingness in the outcome depend on its true value?"),
  rob2_d3_4: (t) =>
    t(
      "Is it likely that missingness in the outcome depended on its true value?"
    ),
  measurement_of_outcome: (t) => t("Bias in measurement of the outcome"),
  rob2_d4_1: (t) => t("Was the method of measuring the outcome inappropriate?"),
  rob2_d4_2: (t) =>
    t(
      "Could measurement or ascertainment of the outcome have differed between intervention groups?"
    ),
  rob2_d4_3: (t) =>
    t(
      "Were outcome assessors aware of the intervention received by study participants?"
    ),
  rob2_d4_4: (t) =>
    t(
      "Could assessment of the outcome have been influenced by knowledge of intervention received?"
    ),
  rob2_d4_5: (t) =>
    t(
      "Is it likely that assessment of the outcome was influenced by knowledge of intervention received?"
    ),
  selection_of_reported_result: (t) =>
    t("Bias in selection of the reported result"),
  rob2_d5_1: (t) =>
    t(
      "Were the data that produced this result analysed in accordance with a pre-specified analysis plan finalized before unblinded outcome data were available?"
    ),
  rob2_d5_2: (t) =>
    t(
      "Is the numerical result being assessed likely to have been selected, on the basis of the results, from multiple eligible outcome measurements (e.g. scales, definitions, time points) within the outcome domain?"
    ),
  rob2_d5_3: (t) =>
    t(
      "Is the numerical result being assessed likely to have been selected, on the basis of the results, from multiple eligible analyses of the data?"
    ),
  // ROBINS-I
  confounding: (t) => t("Bias due to confounding"),
  robins_d1_1: (t) =>
    t(
      "Is there potential for confounding of the effect of intervention in this study?"
    ),
  robins_d1_2: (t) =>
    t(
      "Was the analysis based on splitting participants' follow-up time according to intervention received?"
    ),
  robins_d1_3: (t) =>
    t(
      "Were intervention discontinuations or switches likely to be related to factors that are prognostic for the outcome?"
    ),
  robins_d1_4: (t) =>
    t(
      "Did the authors use an appropriate analysis method that controlled for all the important confounding domains?"
    ),
  robins_d1_5: (t) =>
    t(
      "Were the confounding domains that were controlled for measured validly and reliably by the variables available in this study?"
    ),
  robins_d1_6: (t) =>
    t(
      "Did the authors control for any post-intervention variables that could have been affected by the intervention?"
    ),
  robins_d1_7: (t) =>
    t(
      "Did the authors use an appropriate analysis method that controlled for all the important confounding domains and for time-varying confounding?"
    ),
  robins_d1_8: (t) =>
    t(
      "Were the confounding domains that were controlled for, including time-varying confounding, measured validly and reliably by the variables available in this study?"
    ),
  selection_of_participants: (t) =>
    t("Bias in selection of participants into the study"),
  robins_d2_1: (t) =>
    t(
      "Was selection of participants into the study (or into the analysis) based on participant characteristics observed after the start of intervention?"
    ),
  robins_d2_2: (t) =>
    t(
      "Were the post-intervention variables that influenced selection likely to be associated with intervention?"
    ),
  robins_d2_3: (t) =>
    t(
      "Were the post-intervention variables that influenced selection likely to be influenced by the outcome or a cause of the outcome?"
    ),
  robins_d2_4: (t) =>
    t(
      "Do the start of follow-up and start of intervention coincide for most participants?"
    ),
  robins_d2_5: (t) =>
    t(
      "Were adjustment techniques used that are likely to correct for the presence of selection biases?"
    ),
  classification_of_interventions: (t) =>
    t("Bias in classification of interventions"),
  robins_d3_1: (t) => t("Were intervention groups clearly defined?"),
  robins_d3_2: (t) =>
    t(
      "Was the information used to define intervention groups recorded at the start of the intervention?"
    ),
  robins_d3_3: (t) =>
    t(
      "Could classification of intervention status have been affected by knowledge of the outcome or risk of the outcome?"
    ),
  robins_d4_1: (t) =>
    t(
      "Were there deviations from the intended intervention beyond what would be expected in usual practice?"
    ),
  robins_d4_2: (t) =>
    t(
      "Were these deviations from the intended intervention unbalanced between groups and likely to have affected the outcome?"
    ),
  missing_data: (t) => t("Bias due to missing data"),
  robins_d5_1: (t) =>
    t("Were outcome data available for all, or nearly all, participants?"),
  robins_d5_2: (t) =>
    t("Were participants excluded due to missing data on intervention status?"),
  robins_d5_3: (t) =>
    t(
      "Were participants excluded due to missing data on other variables needed for the analysis?"
    ),
  robins_d5_4: (t) =>
    t(
      "Are the proportion of participants and reasons for missing data similar across interventions?"
    ),
  robins_d5_5: (t) =>
    t(
      "Is there evidence that results were robust to the presence of missing data?"
    ),
  measurement_of_outcomes: (t) => t("Bias in measurement of outcomes"),
  robins_d6_1: (t) =>
    t(
      "Could the outcome measure have been influenced by knowledge of the intervention received?"
    ),
  robins_d6_2: (t) =>
    t(
      "Were outcome assessors aware of the intervention received by study participants?"
    ),
  robins_d6_3: (t) =>
    t(
      "Were the methods of outcome assessment comparable across intervention groups?"
    ),
  robins_d6_4: (t) =>
    t(
      "Were any systematic errors in measurement of the outcome related to intervention received?"
    ),
  robins_d7_1: (t) =>
    t(
      "Is the reported effect estimate likely to have been selected, on the basis of the results, from multiple outcome measurements within the outcome domain?"
    ),
  robins_d7_2: (t) =>
    t(
      "Is the reported effect estimate likely to have been selected, on the basis of the results, from multiple analyses of the intervention-outcome relationship?"
    ),
  robins_d7_3: (t) =>
    t(
      "Is the reported effect estimate likely to have been selected, on the basis of the results, from different subgroups?"
    ),
  // Newcastle–Ottawa Scale
  selection: (t) => t("Selection"),
  nos_s1: (t) =>
    t(
      "Cohort: was the exposed cohort truly or somewhat representative of the average population of interest? Case-control: were the cases defined adequately, with independent validation?"
    ),
  nos_s2: (t) =>
    t(
      "Cohort: was the non-exposed cohort drawn from the same community as the exposed cohort? Case-control: were the cases a consecutive or obviously representative series?"
    ),
  nos_s3: (t) =>
    t(
      "Cohort: was exposure ascertained from a secure record or a structured interview? Case-control: were the controls drawn from the community, not from hospital patients?"
    ),
  nos_s4: (t) =>
    t(
      "Cohort: was it demonstrated that the outcome of interest was not present at the start of the study? Case-control: were the controls defined as having no history of the outcome?"
    ),
  comparability: (t) => t("Comparability"),
  nos_c1: (t) =>
    t(
      "Did the study control for the most important confounding factor, by design (matching, restriction) or by analysis (adjustment)?"
    ),
  nos_c2: (t) =>
    t("Did the study control for any additional important confounding factor?"),
  outcome_or_exposure: (t) => t("Outcome (cohort) / Exposure (case-control)"),
  nos_o1: (t) =>
    t(
      "Cohort: was the outcome assessed by independent blind assessment or by record linkage? Case-control: was exposure ascertained from a secure record or a blinded structured interview?"
    ),
  nos_o2: (t) =>
    t(
      "Cohort: was the follow-up long enough for the outcomes to occur? Case-control: was the same method of ascertainment used for cases and controls?"
    ),
  nos_o3: (t) =>
    t(
      "Cohort: was the follow-up complete, or was the loss to follow-up small and unlikely to introduce bias (about 20% or less, or described and balanced)? Case-control: was the non-response rate the same for both groups?"
    ),
  // JBI Critical Appraisal Checklist (analytical cross-sectional)
  jbi_1: (t) =>
    t("Were the criteria for inclusion in the sample clearly defined?"),
  jbi_1_1: (t) =>
    t("Were the criteria for inclusion in the sample clearly defined?"),
  jbi_2: (t) =>
    t("Were the study subjects and the setting described in detail?"),
  jbi_2_1: (t) =>
    t("Were the study subjects and the setting described in detail?"),
  jbi_3: (t) => t("Was the exposure measured in a valid and reliable way?"),
  jbi_3_1: (t) => t("Was the exposure measured in a valid and reliable way?"),
  jbi_4: (t) =>
    t(
      "Were objective, standard criteria used for measurement of the condition?"
    ),
  jbi_4_1: (t) =>
    t(
      "Were objective, standard criteria used for measurement of the condition?"
    ),
  jbi_5: (t) => t("Were confounding factors identified?"),
  jbi_5_1: (t) => t("Were confounding factors identified?"),
  jbi_6: (t) => t("Were strategies to deal with confounding factors stated?"),
  jbi_6_1: (t) => t("Were strategies to deal with confounding factors stated?"),
  jbi_7: (t) => t("Were the outcomes measured in a valid and reliable way?"),
  jbi_7_1: (t) => t("Were the outcomes measured in a valid and reliable way?"),
  jbi_8: (t) => t("Was appropriate statistical analysis used?"),
  jbi_8_1: (t) => t("Was appropriate statistical analysis used?"),
  // QUADAS-2
  patient_selection: (t) => t("Patient selection"),
  quadas_d1_1: (t) =>
    t("Was a consecutive or random sample of patients enrolled?"),
  quadas_d1_2: (t) => t("Was a case-control design avoided?"),
  quadas_d1_3: (t) => t("Did the study avoid inappropriate exclusions?"),
  index_test: (t) => t("Index test"),
  quadas_d2_1: (t) =>
    t(
      "Were the index test results interpreted without knowledge of the results of the reference standard?"
    ),
  quadas_d2_2: (t) => t("If a threshold was used, was it pre-specified?"),
  reference_standard: (t) => t("Reference standard"),
  quadas_d3_1: (t) =>
    t(
      "Is the reference standard likely to correctly classify the target condition?"
    ),
  quadas_d3_2: (t) =>
    t(
      "Were the reference standard results interpreted without knowledge of the results of the index test?"
    ),
  flow_and_timing: (t) => t("Flow and timing"),
  quadas_d4_1: (t) =>
    t(
      "Was there an appropriate interval between the index test and the reference standard?"
    ),
  quadas_d4_2: (t) => t("Did all patients receive a reference standard?"),
  quadas_d4_3: (t) =>
    t("Did all patients receive the same reference standard?"),
  quadas_d4_4: (t) => t("Were all patients included in the analysis?"),
  // ROBIS
  study_eligibility_criteria: (t) => t("Study eligibility criteria"),
  robis_d1_1: (t) =>
    t(
      "Did the review adhere to pre-defined objectives and eligibility criteria?"
    ),
  robis_d1_2: (t) =>
    t("Were the eligibility criteria appropriate for the review question?"),
  robis_d1_3: (t) => t("Were eligibility criteria unambiguous?"),
  robis_d1_4: (t) =>
    t(
      "Were all restrictions in eligibility criteria based on study characteristics appropriate (e.g. date, sample size, study quality, outcomes measured)?"
    ),
  robis_d1_5: (t) =>
    t(
      "Were any restrictions in eligibility criteria based on sources of information appropriate (e.g. publication status or format, language, availability of data)?"
    ),
  identification_and_selection: (t) =>
    t("Identification and selection of studies"),
  robis_d2_1: (t) =>
    t(
      "Did the search include an appropriate range of databases and electronic sources for published and unpublished reports?"
    ),
  robis_d2_2: (t) =>
    t(
      "Were methods additional to database searching used to identify relevant reports?"
    ),
  robis_d2_3: (t) =>
    t(
      "Were the terms and structure of the search strategy likely to retrieve as many eligible studies as possible?"
    ),
  robis_d2_4: (t) =>
    t(
      "Were restrictions based on date, publication format, or language appropriate?"
    ),
  robis_d2_5: (t) =>
    t(
      "Were efforts made to minimise error in selection of studies (e.g. two independent reviewers)?"
    ),
  data_collection_and_appraisal: (t) =>
    t("Data collection and study appraisal"),
  robis_d3_1: (t) =>
    t(
      "Were efforts made to minimise error in data collection (e.g. extraction in duplicate)?"
    ),
  robis_d3_2: (t) =>
    t(
      "Were sufficient study characteristics available for both review authors and readers to be able to interpret the results?"
    ),
  robis_d3_3: (t) =>
    t("Were all relevant study results collected for use in the synthesis?"),
  robis_d3_4: (t) =>
    t(
      "Was risk of bias (or methodological quality) formally assessed using appropriate criteria?"
    ),
  robis_d3_5: (t) =>
    t(
      "Were efforts made to minimise error in risk of bias assessment (e.g. assessment in duplicate)?"
    ),
  synthesis_and_findings: (t) => t("Synthesis and findings"),
  robis_d4_1: (t) => t("Did the synthesis include all studies that it should?"),
  robis_d4_2: (t) =>
    t("Were all pre-defined analyses reported or departures explained?"),
  robis_d4_3: (t) =>
    t(
      "Was the synthesis appropriate given the nature and similarity in the research questions, study designs and outcomes across included studies?"
    ),
  robis_d4_4: (t) =>
    t(
      "Was between-study variation (heterogeneity) minimal or addressed in the synthesis?"
    ),
  robis_d4_5: (t) =>
    t(
      "Were the findings robust, e.g. as demonstrated through funnel plot or sensitivity analyses?"
    ),
  robis_d4_6: (t) =>
    t("Were biases in primary studies minimal or addressed in the synthesis?"),
  // CASP Qualitative Checklist
  casp_1: (t) => t("Was there a clear statement of the aims of the research?"),
  casp_1_1: (t) =>
    t("Was there a clear statement of the aims of the research?"),
  casp_2: (t) => t("Is a qualitative methodology appropriate?"),
  casp_2_1: (t) => t("Is a qualitative methodology appropriate?"),
  casp_3: (t) =>
    t(
      "Was the research design appropriate to address the aims of the research?"
    ),
  casp_3_1: (t) =>
    t(
      "Was the research design appropriate to address the aims of the research?"
    ),
  casp_4: (t) =>
    t("Was the recruitment strategy appropriate to the aims of the research?"),
  casp_4_1: (t) =>
    t("Was the recruitment strategy appropriate to the aims of the research?"),
  casp_5: (t) =>
    t("Was the data collected in a way that addressed the research issue?"),
  casp_5_1: (t) =>
    t("Was the data collected in a way that addressed the research issue?"),
  casp_6: (t) =>
    t(
      "Has the relationship between researcher and participants been adequately considered?"
    ),
  casp_6_1: (t) =>
    t(
      "Has the relationship between researcher and participants been adequately considered?"
    ),
  casp_7: (t) => t("Have ethical issues been taken into consideration?"),
  casp_7_1: (t) => t("Have ethical issues been taken into consideration?"),
  casp_8: (t) => t("Was the data analysis sufficiently rigorous?"),
  casp_8_1: (t) => t("Was the data analysis sufficiently rigorous?"),
  casp_9: (t) => t("Is there a clear statement of findings?"),
  casp_9_1: (t) => t("Is there a clear statement of findings?"),
  casp_10: (t) => t("How valuable is the research?"),
  casp_10_1: (t) => t("How valuable is the research?"),
  // QUIPS
  study_participation: (t) => t("Study participation"),
  quips_d1_1: (t) =>
    t(
      "Is the source population (the population of interest) adequately described?"
    ),
  quips_d1_2: (t) =>
    t(
      "Are the baseline characteristics of the study sample adequately described (key characteristics)?"
    ),
  quips_d1_3: (t) =>
    t(
      "Are the sampling frame and the recruitment method adequately described?"
    ),
  quips_d1_4: (t) =>
    t("Are the period and the place of recruitment adequately described?"),
  quips_d1_5: (t) =>
    t("Are the inclusion and exclusion criteria adequately described?"),
  quips_d1_6: (t) =>
    t(
      "Is there adequate participation in the study by eligible persons (the sample represents the source population)?"
    ),
  study_attrition: (t) => t("Study attrition"),
  quips_d2_1: (t) =>
    t(
      "Is the proportion of the baseline sample available for analysis adequate (response rate, completeness of follow-up)?"
    ),
  quips_d2_2: (t) =>
    t(
      "Were attempts made to collect information on participants who dropped out of the study?"
    ),
  quips_d2_3: (t) => t("Are the reasons for loss to follow-up provided?"),
  quips_d2_4: (t) =>
    t(
      "Are the participants lost to follow-up adequately described (key characteristics)?"
    ),
  quips_d2_5: (t) =>
    t(
      "Are there no important differences between the participants who completed the study and those who did not?"
    ),
  prognostic_factor_measurement: (t) => t("Prognostic factor measurement"),
  quips_d3_1: (t) =>
    t(
      "Is a clear definition or description of the prognostic factor provided (including dose, level, duration of exposure, and clear specification of the method of measurement)?"
    ),
  quips_d3_2: (t) =>
    t(
      "Was the prognostic factor measured in the same way for all participants?"
    ),
  quips_d3_3: (t) =>
    t(
      "Was the method and setting of measurement of the prognostic factor the same for all study participants?"
    ),
  quips_d3_4: (t) =>
    t(
      "Was an adequate proportion of the study sample complete for the prognostic factor data?"
    ),
  quips_d3_5: (t) =>
    t(
      "Were appropriate methods of imputation used for missing prognostic factor data, or was no imputation needed?"
    ),
  quips_d3_6: (t) =>
    t(
      "Is the measurement of the prognostic factor valid and reliable (validated instruments, established cut-points)?"
    ),
  outcome_measurement: (t) => t("Outcome measurement"),
  quips_d4_1: (t) =>
    t(
      "Is a clear definition of the outcome provided, including duration of follow-up and level and extent of the outcome construct?"
    ),
  quips_d4_2: (t) =>
    t(
      "Is the method of outcome measurement valid and reliable, and blinded to the prognostic factor where relevant?"
    ),
  quips_d4_3: (t) =>
    t(
      "Were the method and setting of outcome measurement the same for all study participants?"
    ),
  study_confounding: (t) => t("Study confounding"),
  quips_d5_1: (t) =>
    t("Were all important confounders, including treatments, measured?"),
  quips_d5_2: (t) =>
    t("Are clear definitions of the important confounders measured provided?"),
  quips_d5_3: (t) =>
    t("Was the measurement of all important confounders valid and reliable?"),
  quips_d5_4: (t) =>
    t(
      "Were the method and setting of confounder measurement the same for all study participants?"
    ),
  quips_d5_5: (t) =>
    t(
      "Were appropriate methods used for missing data on confounders, or was no imputation needed?"
    ),
  quips_d5_6: (t) =>
    t(
      "Were important confounders accounted for in the study design (matching, stratification, restriction)?"
    ),
  quips_d5_7: (t) =>
    t(
      "Were important confounders accounted for in the analysis (multivariable adjustment)?"
    ),
  statistical_analysis_and_reporting: (t) =>
    t("Statistical analysis and reporting"),
  quips_d6_1: (t) =>
    t(
      "Is there sufficient presentation of data to assess the adequacy of the analysis (effect estimates with confidence intervals, numbers)?"
    ),
  quips_d6_2: (t) =>
    t(
      "Was the strategy for model building (inclusion of variables) appropriate and based on a conceptual framework or model?"
    ),
  quips_d6_3: (t) =>
    t(
      "Was the selected statistical model adequate for the design of the study?"
    ),
  quips_d6_4: (t) =>
    t(
      "Is there no selective reporting of results (all planned analyses and outcomes reported)?"
    ),
};

/**
 * One grid item as the reader reads it. `registryText` is the api's English for
 * the same item, used when the table does not know the id.
 */
export function appraisalGridText(
  t: Translate,
  id: string,
  registryText: string
): string {
  const translate = GRID_TEXT[id];
  return translate === undefined ? registryText : translate(t);
}
